import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Upload, FileCheck2, Package, ShieldCheck, Layers, Cog, Zap } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { MATERIALS, SAMPLE_GALLERY } from '../data/mock';

const Home = () => {
  const { t } = useLang();

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-orange-500/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-orange-500/5 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Badge className="bg-orange-500/10 text-orange-600 border border-orange-500/30 hover:bg-orange-500/15 mb-6 px-3 py-1 text-xs tracking-wider">
              {t.home.heroTag}
            </Badge>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight text-zinc-900">
              {t.home.heroTitle}{' '}
              <span className="text-orange-500">{t.home.heroTitleAccent}</span>{' '}
              {t.home.heroTitleEnd}
            </h1>
            <p className="mt-6 text-zinc-600 text-lg leading-relaxed max-w-xl">
              {t.home.heroSubtitle}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/quote">
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-semibold border-0 h-12 px-6 group">
                  {t.home.ctaPrimary}
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="bg-white border-zinc-300 text-zinc-900 hover:bg-zinc-50 hover:text-orange-600 hover:border-orange-400 h-12 px-6">
                  {t.home.ctaSecondary}
                </Button>
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl">
              {[
                { v: '15K+', l: t.home.statsParts },
                { v: '2-5', l: t.home.statsDelivery },
                { v: '±0.1mm', l: t.home.statsAccuracy },
                { v: '7/24', l: t.home.statsSupport }
              ].map((s, i) => (
                <div key={i} className="border-l-2 border-orange-500/60 pl-3">
                  <div className="text-2xl md:text-3xl font-bold text-zinc-900">{s.v}</div>
                  <div className="text-xs text-zinc-500 mt-1 uppercase tracking-wider">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-zinc-200 shadow-xl">
              <img
                src="https://images.pexels.com/photos/12268465/pexels-photo-12268465.jpeg"
                alt="SLA 3D printed part"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                <div>
                  <div className="text-xs text-orange-300 uppercase tracking-widest">SLA</div>
                  <div className="text-white font-semibold">Resin · Precision</div>
                </div>
                <div className="flex items-center gap-1 bg-orange-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-md">
                  <ShieldCheck className="w-3.5 h-3.5" /> ROLVIA
                </div>
              </div>
            </div>

            <div className="absolute -bottom-6 -left-6 hidden md:block bg-white border border-zinc-200 rounded-xl p-4 w-56 shadow-xl">
              <div className="flex items-center gap-2 text-xs text-zinc-500"><Zap className="w-3.5 h-3.5 text-orange-500" /> {t.home.step2Title}</div>
              <div className="mt-2 text-zinc-900 font-semibold text-lg">~ 24 saat</div>
              <div className="mt-1 text-xs text-zinc-500">{t.home.statsDelivery}: 2-5 {t.quote.days}</div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 border-t border-zinc-200 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900">{t.home.howTitle}</h2>
            <p className="mt-3 text-zinc-600">{t.home.howSubtitle}</p>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {[
              { icon: Upload, title: t.home.step1Title, desc: t.home.step1Desc, n: '01' },
              { icon: FileCheck2, title: t.home.step2Title, desc: t.home.step2Desc, n: '02' },
              { icon: Package, title: t.home.step3Title, desc: t.home.step3Desc, n: '03' }
            ].map((s, i) => (
              <Card key={i} className="group bg-white border-zinc-200 hover:border-orange-400 hover:shadow-md transition-all p-6 relative overflow-hidden">
                <div className="absolute top-3 right-4 text-6xl font-black text-zinc-100 group-hover:text-orange-100 transition-colors">{s.n}</div>
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-orange-500/10 text-orange-500 border border-orange-500/20 mb-5">
                  <s.icon className="w-5 h-5" />
                </div>
                <h3 className="text-zinc-900 font-semibold text-xl">{s.title}</h3>
                <p className="mt-2 text-zinc-600 text-sm leading-relaxed">{s.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* TECHNOLOGIES */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900">{t.home.techTitle}</h2>
            <p className="mt-3 text-zinc-600">{t.home.techSubtitle}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: t.home.slaTitle, desc: t.home.slaDesc, img: 'https://images.pexels.com/photos/6502310/pexels-photo-6502310.jpeg', icon: Cog,    tag: 'SLA' },
              { title: t.home.slsTitle, desc: t.home.slsDesc, img: 'https://images.pexels.com/photos/19124461/pexels-photo-19124461.jpeg', icon: Zap,    tag: 'SLS' },
              { title: t.home.fdmTitle, desc: t.home.fdmDesc, img: 'https://images.pexels.com/photos/17509941/pexels-photo-17509941.jpeg', icon: Layers, tag: 'FDM' }
            ].map((tech, i) => (
              <div key={i} className="group relative rounded-2xl overflow-hidden border border-zinc-200 shadow-sm hover:shadow-lg transition-shadow">
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={tech.img} alt={tech.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <div className="flex items-center gap-2 text-xs text-orange-300 uppercase tracking-widest mb-2">
                    <tech.icon className="w-3.5 h-3.5" /> {tech.tag}
                  </div>
                  <h3 className="text-2xl font-bold text-white">{tech.title}</h3>
                  <p className="mt-2 text-zinc-200 text-sm">{tech.desc}</p>
                  <Link to="/services" className="mt-4 inline-flex items-center gap-1.5 text-orange-300 hover:text-orange-200 font-medium text-sm">
                    {t.home.learnMore} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MATERIALS */}
      <section className="py-20 border-t border-zinc-200 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900">{t.home.materialsTitle}</h2>
            <p className="mt-3 text-zinc-600">{t.home.materialsSubtitle}</p>
          </div>

          <Tabs defaultValue="sla" className="w-full">
            <TabsList className="bg-white border border-zinc-200 mx-auto flex w-fit">
              <TabsTrigger value="sla" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white px-6">SLA</TabsTrigger>
              <TabsTrigger value="sls" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white px-6">SLS</TabsTrigger>
              <TabsTrigger value="fdm" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white px-6">FDM</TabsTrigger>
            </TabsList>
            {['sla', 'sls', 'fdm'].map((techKey) => (
              <TabsContent key={techKey} value={techKey} className="mt-8">
                <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {MATERIALS[techKey].map((m) => (
                    <div key={m.id} className="group bg-white border border-zinc-200 hover:border-orange-400 hover:shadow-md transition-all rounded-lg p-4">
                      <div className="w-8 h-8 rounded-md bg-orange-500/10 border border-orange-500/20 flex items-center justify-center mb-3">
                        <Layers className="w-4 h-4 text-orange-500" />
                      </div>
                      <div className="text-zinc-900 font-semibold text-sm">{m.label}</div>
                      <div className="text-xs text-zinc-500 mt-1 uppercase tracking-wider">{techKey}</div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900">{t.home.galleryTitle}</h2>
            <p className="mt-3 text-zinc-600">{t.home.gallerySubtitle}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SAMPLE_GALLERY.map((g) => (
              <div key={g.id} className="group relative aspect-square rounded-xl overflow-hidden border border-zinc-200 shadow-sm hover:shadow-lg transition-shadow">
                <img src={g.img} alt={g.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute top-3 left-3">
                  <span className="bg-white/95 backdrop-blur text-zinc-900 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-md border border-zinc-200">
                    {g.tech}
                  </span>
                </div>
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/85 to-transparent">
                  <div className="text-orange-300 text-[10px] uppercase tracking-widest">{g.material}</div>
                  <div className="text-white font-semibold text-sm">{g.title}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REFERENCES - SCROLLING LOGOS */}
      <section className="py-16 bg-white border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.25em] text-zinc-500 mb-10">
            {t.home.brandsTitle}
          </p>
        </div>
        <div className="relative w-full overflow-hidden rolvia-marquee-mask">
          <div className="rolvia-marquee flex items-center gap-12 w-max">
            {[...t.home.brands, ...t.home.brands].map((b, i) => (
              <div
                key={i}
                className="shrink-0 h-14 px-8 flex items-center justify-center"
              >
                <span className="text-zinc-400 hover:text-orange-500 transition-colors font-bold tracking-tight text-xl md:text-2xl whitespace-nowrap select-none"
                  style={{
                    fontFamily: i % 3 === 0 ? "'Georgia', serif" : i % 3 === 1 ? "system-ui, -apple-system" : "'Courier New', monospace",
                    fontStyle: i % 4 === 0 ? 'italic' : 'normal',
                    letterSpacing: i % 2 === 0 ? '-0.02em' : '0.05em'
                  }}
                >
                  {b}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl border border-orange-200 bg-gradient-to-br from-orange-50 to-white p-10 md:p-14 text-center">
            <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-orange-500/15 blur-3xl" />
            <h2 className="relative text-3xl md:text-4xl font-bold text-zinc-900">{t.home.ctaTitle}</h2>
            <p className="relative mt-3 text-zinc-600 max-w-xl mx-auto">{t.home.ctaDesc}</p>
            <Link to="/quote" className="relative inline-block mt-6">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-semibold border-0 h-12 px-7">
                {t.home.ctaButton} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
